import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Plus,
  Trash,
  PencilSimple,
  ArrowRight,
  Truck,
  CheckCircle,
  Clock,
  CalendarBlank,
  X,
  Check,
  CaretDown,
} from "@phosphor-icons/react";

const STATUS_META = {
  in_transit: {
    label: "В пути",
    cardBorder: "border-amber-200",
    headerBg: "bg-amber-50",
    headerBorder: "border-amber-200",
    badge: "bg-amber-100 text-amber-800 border border-amber-300",
    icon: Truck,
  },
  planned: {
    label: "Запланировано",
    cardBorder: "border-[#d4d4d8]",
    headerBg: "bg-[#f5f5f4]",
    headerBorder: "border-[#d4d4d8]",
    badge: "bg-[#f5f5f4] text-[#52525b] border border-[#d4d4d8]",
    icon: Clock,
  },
  received: {
    label: "Получено",
    cardBorder: "border-green-200",
    headerBg: "bg-green-50",
    headerBorder: "border-green-200",
    badge: "bg-green-100 text-green-800 border border-green-200",
    icon: CheckCircle,
  },
};

const CURRENCY_SYMBOL = { RUB: "₽", USD: "$", EUR: "€" };
const CURRENCIES = ["RUB", "USD", "EUR"];

const emptyForm = () => ({
  from_location: "",
  to_location: "",
  status: "planned",
  arrival_date: "",
  items: [{ name: "", quantity: 1 }],
  costs: [{ description: "", amount: "", currency: "RUB", paid: false }],
  prepayments: [],
  notes: "",
});

function sumByCurrency(costs, filter) {
  const totals = {};
  for (const c of costs) {
    if (filter !== undefined && !!c.paid !== filter) continue;
    const amt = parseFloat(c.amount) || 0;
    if (amt > 0) totals[c.currency] = (totals[c.currency] || 0) + amt;
  }
  return totals;
}

function formatCurrencies(totals) {
  return Object.entries(totals)
    .map(([cur, amt]) => `${amt.toLocaleString("ru-RU")} ${CURRENCY_SYMBOL[cur] || cur}`)
    .join(" + ");
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split("-");
    return new Date(Number(y), Number(m) - 1, Number(d))
      .toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ShipmentsPage() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shipments");
      setShipments(data);
    } catch {
      toast.error("Не удалось загрузить поставки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCostPaid = useCallback(async (shipmentId, costIdx) => {
    // Optimistic update
    setShipments(prev => prev.map(s => {
      if (s.id !== shipmentId) return s;
      const costs = s.costs.map((c, i) => i === costIdx ? { ...c, paid: !c.paid } : c);
      return { ...s, costs };
    }));
    try {
      const { data } = await api.patch(`/shipments/${shipmentId}/costs/${costIdx}/paid`);
      setShipments(prev => prev.map(s => s.id === data.id ? data : s));
    } catch {
      toast.error("Ошибка обновления");
      load(); // revert
    }
  }, [load]);

  const setStatus = useCallback(async (shipmentId, status) => {
    // Optimistic update
    setShipments(prev => prev.map(s => s.id === shipmentId ? { ...s, status } : s));
    try {
      const { data } = await api.patch(`/shipments/${shipmentId}/status`, { status });
      setShipments(prev => prev.map(s => s.id === data.id ? data : s));
      toast.success(`Статус изменён: ${STATUS_META[status].label}`);
    } catch {
      toast.error("Ошибка обновления статуса");
      load(); // revert
    }
  }, [load]);

  const setAllPaid = useCallback(async (shipmentId, paid) => {
    // Optimistic update
    setShipments(prev => prev.map(s => {
      if (s.id !== shipmentId) return s;
      return { ...s, costs: s.costs.map(c => ({ ...c, paid })) };
    }));
    try {
      const { data } = await api.patch(`/shipments/${shipmentId}/paid`, { paid });
      setShipments(prev => prev.map(s => s.id === data.id ? data : s));
      toast.success(paid ? "Поставка отмечена оплаченной" : "Отметки оплаты сняты");
    } catch {
      toast.error("Ошибка обновления");
      load(); // revert
    }
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setFormData(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      from_location: item.from_location,
      to_location: item.to_location,
      status: item.status,
      arrival_date: item.arrival_date || "",
      items: item.items.length ? item.items.map(i => ({ ...i })) : [{ name: "", quantity: 1 }],
      costs: item.costs.length
        ? item.costs.map(c => ({ ...c, amount: String(c.amount) }))
        : [{ description: "", amount: "", currency: "RUB", paid: false }],
      prepayments: (item.prepayments || []).map(p => ({ ...p, amount: String(p.amount) })),
      notes: item.notes || "",
    });
    setFormOpen(true);
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Удалить поставку «${item.from_location} → ${item.to_location}»?`)) return;
    try {
      await api.delete(`/shipments/${item.id}`);
      setShipments(prev => prev.filter(s => s.id !== item.id));
      toast.success("Поставка удалена");
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const onSave = async () => {
    if (!formData.from_location.trim() || !formData.to_location.trim()) {
      toast.error("Укажите маршрут (откуда и куда)");
      return;
    }
    const payload = {
      ...formData,
      items: formData.items.filter(i => i.name.trim()),
      costs: formData.costs
        .filter(c => c.description.trim() && parseFloat(c.amount) > 0)
        .map(c => ({ ...c, amount: parseFloat(c.amount) })),
      prepayments: (formData.prepayments || [])
        .filter(p => parseFloat(p.amount) > 0)
        .map(p => ({ amount: parseFloat(p.amount), currency: p.currency, note: p.note || "" })),
    };
    try {
      if (editing) {
        const { data } = await api.put(`/shipments/${editing.id}`, payload);
        setShipments(prev => prev.map(s => (s.id === data.id ? data : s)));
        toast.success("Поставка обновлена");
      } else {
        const { data } = await api.post("/shipments", payload);
        setShipments(prev => [data, ...prev]);
        toast.success("Поставка добавлена");
      }
      setFormOpen(false);
    } catch {
      toast.error("Ошибка сохранения");
    }
  };

  // Global debt: sum of unpaid costs across all shipments
  const globalDebt = useMemo(() => {
    const totals = {};
    for (const s of shipments) {
      for (const c of s.costs) {
        if (!c.paid) {
          const amt = parseFloat(c.amount) || 0;
          if (amt > 0) totals[c.currency] = (totals[c.currency] || 0) + amt;
        }
      }
    }
    return totals;
  }, [shipments]);

  const stats = {
    inTransit: shipments.filter(s => s.status === "in_transit").length,
    planned: shipments.filter(s => s.status === "planned").length,
    received: shipments.filter(s => s.status === "received").length,
  };

  const hasDebt = Object.values(globalDebt).some(v => v > 0);

  return (
    <div className="p-6 lg:p-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label mb-2">СЕКЦИЯ / БИЗНЕС</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Поставки</h1>
          <p className="text-sm text-[#52525b] mt-2">
            Международные поставки: маршрут, содержимое и расходы.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150"
        >
          <Plus size={14} weight="bold" />
          Добавить поставку
        </button>
      </div>

      {/* Stats + Debt */}
      {shipments.length > 0 && (
        <div className="mt-6 flex gap-3 flex-wrap items-stretch">
          <Chip label="В пути" value={stats.inTransit} accent="text-amber-700" />
          <Chip label="Запланировано" value={stats.planned} />
          <Chip label="Получено" value={stats.received} accent="text-green-700" />
          {hasDebt && (
            <div className="bg-white border border-[#d4d4d8] px-4 py-2 flex items-center gap-3 border-l-4 border-l-[#FF2A00]">
              <div className="mono-label">ОБЩИЙ ДОЛГ</div>
              <div className="font-mono text-lg font-semibold text-[#FF2A00]">
                {formatCurrencies(globalDebt)}
              </div>
            </div>
          )}
          {!hasDebt && shipments.length > 0 && (
            <div className="bg-white border border-[#d4d4d8] px-4 py-2 flex items-center gap-3 border-l-4 border-l-[#00994C]">
              <div className="mono-label">ДОЛГ</div>
              <div className="font-mono text-lg font-semibold text-[#00994C]">Всё оплачено</div>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && <div className="mt-8 mono-label">ЗАГРУЗКА...</div>}

      {/* Empty */}
      {!loading && shipments.length === 0 && (
        <div className="mt-8 bg-white border border-[#d4d4d8] p-10 text-center">
          <Truck size={32} weight="duotone" className="mx-auto text-[#52525b]" />
          <h3 className="text-lg font-medium mt-3">Поставок пока нет</h3>
          <p className="text-sm text-[#52525b] mt-1">Добавьте первую международную поставку</p>
          <button
            onClick={openAdd}
            className="mt-4 inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors"
          >
            <Plus size={14} weight="bold" />
            Добавить поставку
          </button>
        </div>
      )}

      {/* Cards */}
      {!loading && shipments.length > 0 && (
        <div className="mt-6 flex flex-col gap-4">
          {shipments.map(item => (
            <ShipmentCard
              key={item.id}
              item={item}
              onEdit={() => openEdit(item)}
              onDelete={() => onDelete(item)}
              onToggleCost={(idx) => toggleCostPaid(item.id, idx)}
              onSetAllPaid={(paid) => setAllPaid(item.id, paid)}
              onSetStatus={(status) => setStatus(item.id, status)}
            />
          ))}
        </div>
      )}

      {/* Form */}
      {formOpen && (
        <ShipmentForm
          data={formData}
          setData={setFormData}
          editing={editing}
          onClose={() => setFormOpen(false)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function Chip({ label, value, accent = "text-[#0a0a0a]" }) {
  return (
    <div className="bg-white border border-[#d4d4d8] px-4 py-2 flex items-center gap-3">
      <div className="mono-label">{label}</div>
      <div className={`font-mono text-lg font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

function ShipmentCard({ item, onEdit, onDelete, onToggleCost, onSetAllPaid, onSetStatus }) {
  const meta = STATUS_META[item.status] || STATUS_META.planned;
  const Icon = meta.icon;
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef(null);

  useEffect(() => {
    if (!statusOpen) return;
    const onDoc = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [statusOpen]);

  const dateLabel = item.status === "received" ? "Получено" : item.status === "in_transit" ? "Ожидается" : "Запланировано";

  const unpaidTotals = sumByCurrency(item.costs, false);
  const paidTotals = sumByCurrency(item.costs, true);
  const prepaidTotals = {};
  for (const p of item.prepayments || []) {
    const amt = parseFloat(p.amount) || 0;
    if (amt > 0) prepaidTotals[p.currency] = (prepaidTotals[p.currency] || 0) + amt;
  }
  const hasDebt = Object.values(unpaidTotals).some(v => v > 0);
  const allPaid = item.costs.length > 0 && !hasDebt;
  const hasCosts = item.costs.length > 0;

  return (
    <div className={`bg-white border ${meta.cardBorder} overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-5 py-3 ${meta.headerBg} border-b ${meta.headerBorder}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0" ref={statusRef}>
            <button
              onClick={() => setStatusOpen(v => !v)}
              title="Нажмите, чтобы изменить статус"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium ${meta.badge} hover:opacity-80 transition-opacity cursor-pointer`}
            >
              <Icon size={12} weight="fill" />
              {meta.label}
              <CaretDown size={10} weight="bold" />
            </button>
            {statusOpen && (
              <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-[#d4d4d8] shadow-lg min-w-[180px]">
                {Object.entries(STATUS_META).map(([key, m]) => {
                  const MIcon = m.icon;
                  const active = key === item.status;
                  return (
                    <button
                      key={key}
                      onClick={() => { setStatusOpen(false); if (!active) onSetStatus(key); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                        active ? "bg-[#f5f5f4] font-semibold" : "hover:bg-[#f5f5f4]"
                      }`}
                    >
                      <MIcon size={13} weight="fill" className="text-[#52525b]" />
                      {m.label}
                      {active && <Check size={12} weight="bold" className="ml-auto text-[#00994C]" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-base font-semibold truncate">
            <span className="truncate">{item.from_location}</span>
            <ArrowRight size={14} weight="bold" className="text-[#52525b] shrink-0" />
            <span className="truncate">{item.to_location}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-3">
          {item.arrival_date && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#52525b]">
              <CalendarBlank size={12} weight="bold" />
              <span>{dateLabel}: {formatDate(item.arrival_date)}</span>
            </div>
          )}
          {hasCosts && hasDebt && (
            <button
              onClick={() => onSetAllPaid(true)}
              className="inline-flex items-center gap-1.5 text-xs bg-[#00994C] text-white px-3 py-1.5 hover:bg-[#007a3d] transition-colors duration-150"
              title="Отметить всю поставку оплаченной"
            >
              <CheckCircle size={13} weight="fill" />
              Оплатить всё
            </button>
          )}
          {hasCosts && allPaid && (
            <button
              onClick={() => onSetAllPaid(false)}
              className="inline-flex items-center gap-1.5 text-xs border border-[#d4d4d8] text-[#52525b] px-3 py-1.5 hover:bg-[#f5f5f4] transition-colors duration-150"
              title="Снять отметки оплаты со всей поставки"
            >
              <X size={13} weight="bold" />
              Снять оплату
            </button>
          )}
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1 text-xs border border-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors duration-150"
          >
            <PencilSimple size={12} weight="bold" />
            Ред.
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-[#52525b] hover:text-[#FF2A00] transition-colors"
            title="Удалить"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#d4d4d8]">
        {/* Items */}
        <div className="p-5">
          <div className="mono-label mb-3">СОДЕРЖИМОЕ</div>
          {item.items.length === 0 ? (
            <p className="text-sm text-[#52525b]">—</p>
          ) : (
            <div className="space-y-1.5">
              {item.items.map((it, i) => (
                <div key={i} className="flex items-baseline justify-between text-sm gap-4">
                  <span>{it.name}</span>
                  <span className="font-mono text-[#52525b] shrink-0">{it.quantity} шт</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Costs with paid toggles */}
        <div className="p-5">
          <div className="mono-label mb-3">РАСХОДЫ</div>
          {item.costs.length === 0 ? (
            <p className="text-sm text-[#52525b]">—</p>
          ) : (
            <>
              <div className="space-y-2">
                {item.costs.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        onClick={() => onToggleCost(i)}
                        title={c.paid ? "Снять отметку оплаты" : "Отметить оплаченным"}
                        className={`shrink-0 w-5 h-5 border flex items-center justify-center transition-colors duration-150 ${
                          c.paid
                            ? "bg-[#00994C] border-[#00994C] text-white hover:bg-[#007a3d]"
                            : "bg-white border-[#d4d4d8] hover:border-[#0047FF]"
                        }`}
                      >
                        {c.paid && <Check size={11} weight="bold" />}
                      </button>
                      <span className={`text-sm truncate ${c.paid ? "text-[#a1a1aa] line-through" : "text-[#52525b]"}`}>
                        {c.description}
                      </span>
                    </div>
                    <span className={`font-mono text-sm shrink-0 ${c.paid ? "text-[#a1a1aa]" : ""}`}>
                      {(parseFloat(c.amount) || 0).toLocaleString("ru-RU")} {CURRENCY_SYMBOL[c.currency] || c.currency}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-3 pt-3 border-t border-[#d4d4d8] space-y-1.5">
                {Object.keys(paidTotals).length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-[#00994C]">ОПЛАЧЕНО</span>
                    <span className="font-mono text-sm text-[#00994C]">{formatCurrencies(paidTotals)}</span>
                  </div>
                )}
                {(item.prepayments || []).length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-[#0047FF]">ПРЕДОПЛАТА</span>
                    <span className="font-mono text-sm text-[#0047FF]">
                      {formatCurrencies(prepaidTotals)}
                    </span>
                  </div>
                )}
                {hasDebt && (
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-[#FF2A00]">ДОЛГ</span>
                    <span className="font-mono font-semibold text-[#FF2A00]">{formatCurrencies(unpaidTotals)}</span>
                  </div>
                )}
                {allPaid && (
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-[#00994C]">ИТОГО</span>
                    <span className="font-mono font-semibold text-[#00994C]">Всё оплачено ✓</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Notes */}
      {item.notes && (
        <div className="px-5 pb-4 pt-3 border-t border-[#d4d4d8]">
          <p className="text-sm text-[#52525b]">{item.notes}</p>
        </div>
      )}

      {/* Mobile date */}
      {item.arrival_date && (
        <div className="sm:hidden px-5 pb-3 flex items-center gap-1.5 text-xs text-[#52525b] border-t border-[#d4d4d8] pt-3">
          <CalendarBlank size={12} weight="bold" />
          <span>{dateLabel}: {formatDate(item.arrival_date)}</span>
        </div>
      )}
    </div>
  );
}

function ShipmentForm({ data, setData, editing, onClose, onSave }) {
  const set = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const setItem = (i, field, value) => {
    const next = [...data.items];
    next[i] = { ...next[i], [field]: value };
    set("items", next);
  };
  const addItem = () => set("items", [...data.items, { name: "", quantity: 1 }]);
  const removeItem = (i) => set("items", data.items.filter((_, idx) => idx !== i));

  const setCost = (i, field, value) => {
    const next = [...data.costs];
    next[i] = { ...next[i], [field]: value };
    set("costs", next);
  };
  const addCost = () => set("costs", [...data.costs, { description: "", amount: "", currency: "RUB", paid: false }]);
  const removeCost = (i) => set("costs", data.costs.filter((_, idx) => idx !== i));

  const prepays = data.prepayments || [];
  const setPrepay = (i, field, value) => {
    const next = [...prepays];
    next[i] = { ...next[i], [field]: value };
    set("prepayments", next);
  };
  const addPrepay = () => set("prepayments", [...prepays, { amount: "", currency: "RUB", note: "" }]);
  const removePrepay = (i) => set("prepayments", prepays.filter((_, idx) => idx !== i));

  const dateLabel = data.status === "received" ? "ДАТА ПОЛУЧЕНИЯ" : "ДАТА ПРИБЫТИЯ";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8 px-4">
      <div className="bg-white w-full max-w-2xl border border-[#d4d4d8] my-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d4d4d8]">
          <h2 className="text-lg font-semibold">
            {editing ? "Редактировать поставку" : "Новая поставка"}
          </h2>
          <button onClick={onClose} className="text-[#52525b] hover:text-[#0a0a0a] transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Route */}
          <div>
            <div className="mono-label mb-2">МАРШРУТ</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#52525b] mb-1 block">Откуда</label>
                <input
                  type="text"
                  value={data.from_location}
                  onChange={e => set("from_location", e.target.value)}
                  placeholder="Киев"
                  className="w-full border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]"
                />
              </div>
              <div>
                <label className="text-xs text-[#52525b] mb-1 block">Куда</label>
                <input
                  type="text"
                  value={data.to_location}
                  onChange={e => set("to_location", e.target.value)}
                  placeholder="Хургада"
                  className="w-full border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]"
                />
              </div>
            </div>
          </div>

          {/* Status + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mono-label mb-2">СТАТУС</div>
              <select
                value={data.status}
                onChange={e => set("status", e.target.value)}
                className="w-full border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF] bg-white"
              >
                <option value="planned">Запланировано</option>
                <option value="in_transit">В пути</option>
                <option value="received">Получено</option>
              </select>
            </div>
            <div>
              <div className="mono-label mb-2">{dateLabel}</div>
              <input
                type="date"
                value={data.arrival_date}
                onChange={e => set("arrival_date", e.target.value)}
                className="w-full border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]"
              />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="mono-label">СОДЕРЖИМОЕ</div>
              <button
                onClick={addItem}
                className="text-xs text-[#0047FF] hover:underline flex items-center gap-1"
              >
                <Plus size={12} weight="bold" /> Добавить
              </button>
            </div>
            <div className="space-y-2">
              {data.items.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item.name}
                    onChange={e => setItem(i, "name", e.target.value)}
                    placeholder="Название препарата"
                    className="flex-1 border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]"
                  />
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={e => setItem(i, "quantity", Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    className="w-20 border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF] font-mono text-center"
                  />
                  <span className="text-xs text-[#52525b] w-5">шт</span>
                  <button
                    onClick={() => removeItem(i)}
                    disabled={data.items.length === 1}
                    className="text-[#52525b] hover:text-[#FF2A00] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Costs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="mono-label">РАСХОДЫ</div>
              <button
                onClick={addCost}
                className="text-xs text-[#0047FF] hover:underline flex items-center gap-1"
              >
                <Plus size={12} weight="bold" /> Добавить
              </button>
            </div>
            <div className="space-y-2">
              {data.costs.map((cost, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={cost.description}
                    onChange={e => setCost(i, "description", e.target.value)}
                    placeholder="Описание (напр. Курьеру)"
                    className="flex-1 border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]"
                  />
                  <input
                    type="number"
                    value={cost.amount}
                    onChange={e => setCost(i, "amount", e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    className="w-24 border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF] font-mono text-right"
                  />
                  <select
                    value={cost.currency}
                    onChange={e => setCost(i, "currency", e.target.value)}
                    className="w-16 border border-[#d4d4d8] px-2 py-2 text-sm focus:outline-none focus:border-[#0047FF] bg-white"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c} value={c}>{CURRENCY_SYMBOL[c]}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeCost(i)}
                    disabled={data.costs.length === 1}
                    className="text-[#52525b] hover:text-[#FF2A00] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Prepayments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="mono-label">ПРЕДОПЛАТА</div>
              <button
                onClick={addPrepay}
                className="text-xs text-[#0047FF] hover:underline flex items-center gap-1"
              >
                <Plus size={12} weight="bold" /> Добавить
              </button>
            </div>
            {prepays.length === 0 ? (
              <p className="text-xs text-[#52525b]">Уже внесённые суммы. Остаток к оплате считается с их учётом.</p>
            ) : (
              <div className="space-y-2">
                {prepays.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={p.note}
                      onChange={e => setPrepay(i, "note", e.target.value)}
                      placeholder="Комментарий (необязательно)"
                      className="flex-1 border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]"
                    />
                    <input
                      type="number"
                      value={p.amount}
                      onChange={e => setPrepay(i, "amount", e.target.value)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                      className="w-24 border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF] font-mono text-right"
                    />
                    <select
                      value={p.currency}
                      onChange={e => setPrepay(i, "currency", e.target.value)}
                      className="w-16 border border-[#d4d4d8] px-2 py-2 text-sm focus:outline-none focus:border-[#0047FF] bg-white"
                    >
                      {CURRENCIES.map(c => (
                        <option key={c} value={c}>{CURRENCY_SYMBOL[c]}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removePrepay(i)}
                      className="text-[#52525b] hover:text-[#FF2A00] transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <div className="mono-label mb-2">ПРИМЕЧАНИЯ</div>
            <textarea
              value={data.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="Дополнительная информация..."
              rows={2}
              className="w-full border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF] resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#d4d4d8]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#d4d4d8] hover:bg-[#f5f5f4] transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 text-sm bg-[#0047FF] text-white hover:bg-[#0036CC] transition-colors"
          >
            {editing ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </div>
    </div>
  );
}


